// auth.effects.ts

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  catchError,
  map,
  switchMap,
  tap,
  filter,
  take,
  withLatestFrom,
  delay,
  exhaustMap,
  concatMap,
} from 'rxjs/operators';
import { of, interval, timer, EMPTY, throwError } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import {
  SessionActions,
  LoginActions,
  TokenActions,
  UserProfileActions,
  WebhookActions,
  OnboardingActions,
  RegistrationActions,
} from './auth.actions';
import { AppwriteService } from '../../../../services/appwrite.sdk.service';
import { ApiService } from '../../../../services';

import { environment } from 'src/environments/environment';
import { selectUserState } from './auth.selectors';

const TOKEN_REFRESH_BUFFER = 240; // seconds before expiry to refresh
const WEBHOOK_POLL_INTERVAL = 1000; // ms
const WEBHOOK_MAX_ATTEMPTS = 30; // 30 seconds total

@Injectable()
export class AuthEffects {
  private readonly storageKeys = environment.storageKeys;

  constructor(
    private actions$: Actions,
    private store: Store,
    private appwriteService: AppwriteService,
    private api: ApiService,
    private router: Router,
  ) {}

  /**
   * Initialize application state from localStorage
   */
  initializeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.initialize),
      map(() => {
        const token = this.getFromStorage(this.storageKeys.tokenStorageKey);
        const learmoProfile = this.getFromStorage(
          this.storageKeys.currentUserStorageKey,
        );
        const appwriteProfile = this.getFromStorage(
          this.storageKeys.appwriteProfileStorageKey,
        );

        if (this.isValidStorageData(token, learmoProfile, appwriteProfile)) {
          return SessionActions.initializeSuccess({
            token,
            learmoProfile: JSON.parse(learmoProfile),
            appwriteProfile: JSON.parse(appwriteProfile),
          });
        }

        return SessionActions.initializeFailure();
      }),
    ),
  );

  /**
   * Handle successful session initialization
   */
  initializeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.initializeSuccess),
      concatMap(({ token, learmoProfile, appwriteProfile }) => [
        TokenActions.set({ token }),
        UserProfileActions.setLearmoProfile({ learmoProfile }),
        UserProfileActions.setAppwriteProfile({ appwriteProfile }),
      ]),
    ),
  );

  /**
   * Handle email/password login
   */
  loginWithEmailPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.emailPassword),
      exhaustMap(({ email, password, fromRegister }) =>
        this.appwriteService
          .loginWithEmailAndPassword({ email, password })
          .pipe(
            map(() => TokenActions.generate()),
            catchError((error) => of(LoginActions.failure({ error }))),
          ),
      ),
    ),
  );

  /**
   * Generate JWT token after successful login
   */
  generateToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.generate),
      exhaustMap(() =>
        this.appwriteService.createJWT().pipe(
          concatMap((result) => [
            TokenActions.set({ token: result.jwt }),
            WebhookActions.check(),
          ]),
          catchError((error) => of(LoginActions.failure({ error }))),
        ),
      ),
    ),
  );

  /**
   * Handle token refresh
   */
  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.refresh),
      take(1),
      exhaustMap(() =>
        this.appwriteService.createJWT().pipe(
          concatMap((result) => [
            TokenActions.refreshSuccess({ token: result.jwt }),
            TokenActions.cancelScheduledRefresh(),
          ]),
          catchError((error) => of(TokenActions.refreshFailure({ error }))),
        ),
      ),
    ),
  );

  /**
   * Set token and schedule refresh based on expiry
   */
  setToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.set, TokenActions.refreshSuccess),
      withLatestFrom(this.store.select(selectUserState)),
      switchMap(([{ token }, state]) => {
        try {
          const decoded = jwtDecode<{ exp: number }>(token);
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = decoded.exp - currentTime;

          console.log('Token lifetime:', timeUntilExpiry, 'seconds');

          // Token expired or about to expire
          if (timeUntilExpiry < TOKEN_REFRESH_BUFFER) {
            console.log('Token requires immediate refresh');
            return this.appwriteService.refreshToken().pipe(
              concatMap((newToken) => [
                TokenActions.refreshSuccess({ token: newToken }),
                TokenActions.cancelScheduledRefresh(),
              ]),
              catchError((error) => of(LoginActions.failure({ error }))),
            );
          }

          // Schedule future refresh
          if (!state.isTokenRefreshScheduled) {
            const refreshDelay = timeUntilExpiry - TOKEN_REFRESH_BUFFER;
            console.log('Scheduling token refresh in', refreshDelay, 'seconds');

            return timer(refreshDelay * 1000).pipe(
              take(1),
              concatMap(() => [
                TokenActions.scheduleRefresh({ timeout: refreshDelay }),
                TokenActions.refresh(),
              ]),
            );
          }

          return EMPTY;
        } catch (error) {
          console.error('Error decoding token:', error);
          return of(LoginActions.failure({ error: error as Error }));
        }
      }),
    ),
  );

  /**
   * Poll webhook status until registration is complete
   */
  checkWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.check),
      switchMap(() =>
        interval(WEBHOOK_POLL_INTERVAL).pipe(
          switchMap(() => this.appwriteService.isWebhookDone()),
          filter((isComplete) => isComplete),
          take(1),
          map(() => WebhookActions.complete()),
          catchError(() => of(WebhookActions.timeout())),
        ),
      ),
    ),
  );

  /**
   * Handle webhook completion
   */
  webhookComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.complete),
      concatMap(() => [
        UserProfileActions.loadLearmoProfile({ redirect: true }),
        UserProfileActions.loadAppwriteProfile(),
      ]),
    ),
  );

  /**
   * Load Learmo user profile
   */
  loadLearmoProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loadLearmoProfile),
      exhaustMap(({ redirect }) =>
        this.api.me().pipe(
          concatMap((profile) => {
            const actions: any[] = [
              UserProfileActions.setLearmoProfile({ learmoProfile: profile }),
            ];

            if (redirect) {
              actions.push(LoginActions.success());
            }

            return actions;
          }),
          catchError((error) => of(LoginActions.failure({ error }))),
        ),
      ),
    ),
  );

  /**
   * Load Appwrite user profile
   */
  loadAppwriteProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loadAppwriteProfile),
      exhaustMap(() =>
        this.appwriteService.getUserCurrentUserAppwriteProfile().pipe(
          map((profile) =>
            UserProfileActions.setAppwriteProfile({ appwriteProfile: profile }),
          ),
          catchError((error) => of(LoginActions.failure({ error }))),
        ),
      ),
    ),
  );

  /**
   * Handle successful login and navigation
   */
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActions.success),
        withLatestFrom(this.store.select(selectUserState)),
        tap(([_, userState]) => {
          // Note: We no longer dispatch RegistrationActions.sendVerificationEmail() here
          // because the backend register() endpoint already sends the email natively.
          // Sending it again would overwrite the session_code in the DB, invalidating the first email.
          this.navigateAfterLogin(userState.learmoProfile);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Register new user
   */
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.register),
      exhaustMap(({ firstname, lastname, email, password, trackingCode }) =>
        this.api
          .register({
            firstname,
            lastname,
            email,
            password,
            confirmPassword: password,
            ...(trackingCode ? { trackingCode } : {}),
          })
          .pipe(
            delay(5000), // Wait for backend processing
            map(() => RegistrationActions.registerSuccess({ email, password })),
            catchError((error) =>
              of(RegistrationActions.registerFailure({ error })),
            ),
          ),
      ),
    ),
  );

  /**
   * Auto-login after successful registration
   */
  registerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.registerSuccess),
      map(({ email, password }) =>
        LoginActions.emailPassword({ email, password, fromRegister: true }),
      ),
    ),
  );

  /**
   * Send email verification
   */
  sendVerificationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.sendVerificationEmail),
      exhaustMap(() =>
        this.api.resendVerificationEmail().pipe(
          map((result) =>
            RegistrationActions.verificationEmailSent({ result }),
          ),
          catchError((error) =>
            of(RegistrationActions.verificationEmailFailed({ error })),
          ),
        ),
      ),
    ),
  );

  /**
   * Mark website onboarding as complete
   */
  markOnboardingDone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.markWebsiteSetupDone),
      exhaustMap(() =>
        this.api.markWebsiteOnboarding(true).pipe(
          map(() => OnboardingActions.updateSuccess({ isComplete: true })),
          catchError((error) => of(OnboardingActions.updateFailure({ error }))),
        ),
      ),
    ),
  );

  /**
   * Clear session and navigate to login
   */
  forgetSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.forget),
        tap(({ redirect }) => {
          this.clearStorage();
          this.appwriteService.clearAppwriteSession();
          this.appwriteService.regenerateAppwriteClient();

          if (redirect) {
            this.router.navigate(['auth/login']);
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * Helper: Navigate user based on role and subscription
   */
  private navigateAfterLogin(profile: any): void {
    if (!profile) {
      this.router.navigate(['dashboard']);
      return;
    }

    switch (profile.role?.name) {
      case 'subscriber':
        if (profile.subscription?.isSubscribed) {
          this.router.navigate(['dashboard']);
        } else {
          this.router.navigate(['choose-plan']);
        }
        break;
      case 'affiliate':
        this.router.navigate(['affiliate']);
        break;
      default:
        this.router.navigate(['dashboard']);
    }
  }

  /**
   * Helper: Get item from localStorage safely
   */
  private getFromStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * Helper: Validate storage data
   */
  private isValidStorageData(...items: (string | null)[]): boolean {
    return items.every(
      (item) => item !== null && item !== 'undefined' && item.trim() !== '',
    );
  }

  /**
   * Helper: Clear all auth-related storage
   */
  private clearStorage(): void {
    localStorage.removeItem(this.storageKeys.currentUserStorageKey);
    localStorage.removeItem(this.storageKeys.appwriteProfileStorageKey);
    localStorage.removeItem(this.storageKeys.tokenStorageKey);
  }
}
